/**
 * How a BLE ping's raw value keys relate to the dashboard's parameters.
 *
 * A BLE ping carries whatever the Pi broadcast, under the sensor firmware's own
 * names - `co2_ppm` on one build, `co2` on another; `pn03_b`/`pn03_m`/`pn03_t`
 * for the Duet's bottom/mid/top SPS30s. The dashboard's threshold configuration,
 * by contrast, is keyed by {@link ParametersType} (`co2`, `pn03_m`, `pm25_m`, ...)
 * - the names stored in `client_details.config.*.thresholds` - so anything that
 * wants to colour a BLE reading by its threshold has to translate first.
 *
 * Keys deliberately NOT mapped. `voc_index` is a 1-500 index, not the ppb that
 * the `tvoc` thresholds mean, so mapping it would compare an index against a
 * concentration limit. `pm1_0` has no dashboard parameter, so there is no
 * threshold to use. `aria_binned`, `channel`, `battery_v`, `uptime_s`, `status`
 * and `heater_on` are not air-quality readings at all.
 *
 * An unmapped key is not an error: it simply has no threshold, and callers fall
 * back to a plain colour scale.
 */

import { ParametersType } from '../constants/telosair-interfaces';

/**
 * BLE value key -> dashboard parameter.
 *
 * The three Duet height variants all map to the same parameter because they
 * measure the same quantity in the same unit; only the sampling point differs.
 */
const BLE_TO_PARAMETER: Readonly<Record<string, ParametersType>> = {
  co2: 'co2',
  co2_ppm: 'co2',
  temp: 'temp',
  temp_c: 'temp',
  hum: 'hum',
  humidity: 'hum',
  tvoc: 'tvoc',
  pressure: 'pressure',
  pm25_b: 'pm25_m',
  pm25_m: 'pm25_m',
  pm25_t: 'pm25_m',
  pm2_5: 'pm25_m',
  pn03_b: 'pn03_m',
  pn03_m: 'pn03_m',
  pn03_t: 'pn03_m',
  pm10: 'pm100_m',
  pm100_m: 'pm100_m'
};

/**
 * The dashboard parameter a BLE value key reports, or null when the key has no
 * equivalent and therefore no configured threshold.
 * @param key - Raw BLE ping value key.
 * @returns The matching parameter, or null.
 */
export function bleParameterOf(key: string): ParametersType | null {
  return BLE_TO_PARAMETER[key] ?? null;
}

/** Display unit per BLE key, for keys the dashboard's parameter list does not cover. */
const BLE_UNITS: Readonly<Record<string, string>> = {
  co2: 'ppm',
  co2_ppm: 'ppm',
  temp: '°C',
  temp_c: '°C',
  hum: '%',
  humidity: '%',
  tvoc: 'ppb',
  voc_index: 'index',
  pressure: 'hPa',
  pm25_b: 'µg/m³',
  pm25_m: 'µg/m³',
  pm25_t: 'µg/m³',
  pm2_5: 'µg/m³',
  pm1_0: 'µg/m³',
  pm10: 'µg/m³',
  pm100_m: 'µg/m³',
  pn03_b: '#/cm³',
  pn03_m: '#/cm³',
  pn03_t: '#/cm³',
  aria_binned: '#/cm³',
  battery_v: 'V',
  uptime_s: 's'
};

/**
 * Unit for a BLE value key, or '' when it is dimensionless or unknown.
 * @param key - Raw BLE ping value key.
 * @returns The unit string.
 */
export function bleUnitOf(key: string): string {
  return BLE_UNITS[key] ?? '';
}

/**
 * Human label for a BLE value key - `pn03_m` reads as "pn03 m" otherwise.
 * @param key - Raw BLE ping value key.
 * @returns A display label.
 */
export function bleLabelOf(key: string): string {
  const named: Record<string, string> = {
    co2: 'CO₂',
    co2_ppm: 'CO₂',
    temp: 'Temperature',
    temp_c: 'Temperature',
    hum: 'Humidity',
    humidity: 'Humidity',
    tvoc: 'TVOC',
    voc_index: 'VOC index',
    pressure: 'Pressure',
    pm25_b: 'PM2.5 (bottom)',
    pm25_m: 'PM2.5',
    pm25_t: 'PM2.5 (top)',
    pm2_5: 'PM2.5',
    pm1_0: 'PM1',
    pm10: 'PM10',
    pm100_m: 'PM10',
    pn03_b: 'PN0.3 (bottom)',
    pn03_m: 'PN0.3',
    pn03_t: 'PN0.3 (top)',
    aria_binned: 'Binned total',
    battery_v: 'Battery',
    uptime_s: 'Uptime',
    channel_data: 'Size channels'
  };
  return named[key] ?? key.replace(/_/g, ' ');
}
